import { PayloadAction } from '@reduxjs/toolkit';
import { ClaimUserMembershipGetPaginatedResponse } from 'common/services/api/claimUserMembership/claimUserMembership.api';
import { IAPIRoleUserMembershipGetPaginatedSingleResponse } from 'common/services/api/RoleUserMembership/RoleUserMembership.api';
import { TenantUserMembershipGetPaginatedSingleResponse, UserEntitlementsCounts, UserTenantMembershipStatusProps } from 'common/services/api/TenantUserMembership/TenantUserMembershipApiTypeDefs';
import { APIUserGroupsUsersGetSingleResponse } from 'common/services/api/UserGroupsUsers/userGroupsUsers.api';
import { EntitiesStatus } from 'common/types/Entities';
import { defaultFiltersWithStatus, FiltersWithStatus } from 'common/types/FiltersWithStatus';

import { isFutureDate, isPastDate } from './dateHelpers';

export const TOOLTIP_UTMS_STATUS_MESSSAGES = {
    inactiveDueToStartDateInTheFuture: 'This user\'s start date is in the future',
    inactiveDueToEndDateInThePast: 'This user\'s end date is in the past',
    active: 'This user has access to one or more entitlements',
    noAccess: 'This user has access but no active entitlements are assigned',
};

const _isNoAccessStatus = (status: string): boolean => status === EntitiesStatus.NOACCESS;

export const _isUTMHasEntitlements = (
    entitlementCounts: UserEntitlementsCounts
): boolean => {
    const { aclCounts, roleCounts, globalRoleCounts, claimCounts } = entitlementCounts;
    const getNumber = (val?: number) => val ?? 0;
    const totalCount = getNumber(aclCounts) + getNumber(roleCounts) + getNumber(globalRoleCounts) + getNumber(claimCounts);
    return totalCount > 0;
};

export const _calculateStatusTooltipMessage = (
    tooltipProps: UserTenantMembershipStatusProps
): string => {
    const {
        inactiveDueToEndDateInThePast,
        inactiveDueToStartDateInTheFuture,
        active,
        noAccess
    } = TOOLTIP_UTMS_STATUS_MESSSAGES;
    const { startDate, endDate, status } = tooltipProps;
    const { aclCounts, roleCounts, claimCounts, globalRoleCounts } = tooltipProps;
    const userEntitlements = {
        aclCounts,
        roleCounts,
        claimCounts,
        globalRoleCounts
    } as UserEntitlementsCounts;

    if(isFutureDate(startDate)) return inactiveDueToStartDateInTheFuture;
    if(endDate && isPastDate(endDate)) return inactiveDueToEndDateInThePast;
    if(_isUTMHasEntitlements(userEntitlements)) return active;
    if(status && _isNoAccessStatus(status)) return noAccess;

    return '';
};

export const getStatusDisplayName = (status: EntitiesStatus) => {
    switch(status) {
        case EntitiesStatus.ACTIVE:
            return status.toString();
        case EntitiesStatus.INACTIVE:
            return status.toString();
        case EntitiesStatus.NOACCESS:
            return 'No Access';
    }
};

export const getUTMsStatusTooltipProps = (
    row: APIUserGroupsUsersGetSingleResponse | ClaimUserMembershipGetPaginatedResponse |
    IAPIRoleUserMembershipGetPaginatedSingleResponse | TenantUserMembershipGetPaginatedSingleResponse
): UserTenantMembershipStatusProps => {
    const {
        startDate,
        endDate,
        status,
        aclCounts,
        roleCounts,
        claimCounts,
        globalRoleCounts
    } = row;

    return {
        startDate,
        endDate,
        status,
        aclCounts,
        roleCounts,
        claimCounts,
        globalRoleCounts
    };
};


export const statusesList = [
    EntitiesStatus.ACTIVE,
    EntitiesStatus.INACTIVE,
    EntitiesStatus.NOACCESS
];


export function getReducers() {
    return {
        addNewFilter: (state: FiltersWithStatus, action: PayloadAction<string>) => {
            if (!state.filters.keywords.includes(action.payload)) {
                state.filters.keywords.push(action.payload);
            }
        },
        removeFilter: (state : FiltersWithStatus, action: PayloadAction<string>) => {
            const index = state.filters.keywords.indexOf(action.payload);
            if (index > -1) {
                state.filters.keywords.splice(index, 1);
            }
        },
        clearFilters: () => defaultFiltersWithStatus,
        checkStatusFilter: (state: FiltersWithStatus, action: PayloadAction<string>) => {
            const index = state.filters.dropdowns.status.keys.indexOf(action.payload);
            if (index === -1) {
                state.filters.dropdowns.status.keys.push(action.payload);
            } else {
                state.filters.dropdowns.status.keys.splice(index, 1);
            }
        }
    };
}
